// src/supabase.js - Fixed session persistence with RedCap assessments
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://zquoaicefmsdeugdixvs.supabase.co'
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY

console.log('🔍 Supabase Debug: URL:', supabaseUrl);
console.log('🔍 Supabase Debug: Key exists:', !!supabaseKey);
console.log('🔍 Supabase Debug: Key length:', supabaseKey?.length);

// FIXED: Better session persistence configuration
const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // CHANGED: Enable URL detection
    flowType: 'pkce',
    storage: window.localStorage, // ADDED: Explicitly use localStorage
    storageKey: `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`, // ADDED: Explicit storage key
    debug: process.env.NODE_ENV === 'development' // ADDED: Debug in development
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web',
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    enabled: false
  }
});

// Test the connection immediately
const testConnection = async () => {
  try {
    console.log('🔄 Supabase Debug: Testing connection...');
    const { data, error } = await supabase.from('user_sessions').select('count').limit(1);
    if (error) {
      console.error('❌ Supabase Debug: Connection test failed:', error);
    } else {
      console.log('✅ Supabase Debug: Connection test passed');
    }
  } catch (error) {
    console.error('❌ Supabase Debug: Connection test error:', error);
  }
};

testConnection();

// ADDED: Debug session storage
const debugSessionStorage = () => {
  const authKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
  console.log('🔍 Auth storage key:', authKey);
  console.log('🔍 Stored session:', localStorage.getItem(authKey));
};

// Call debug in development
if (process.env.NODE_ENV === 'development') {
  debugSessionStorage();
}

// Helper function to handle 406 errors
const handleSupabaseError = (error, operation) => {
  if (error?.code === '406' || error?.status === 406) {
    console.error(`❌ 406 Error in ${operation}:`, error);
    console.error('This might be a database schema or RLS policy issue');
    return {
      isClientError: true,
      message: 'Database access denied. Please check your permissions.',
      originalError: error
    };
  }
  return {
    isClientError: false,
    message: error?.message || 'Unknown error',
    originalError: error
  };
};

// Enhanced connection test that checks auth endpoint specifically
export const testAuthEndpoint = async () => {
  try {
    console.log('🔍 Testing auth endpoint with multiple methods...');
    
    // Test 1: Direct auth endpoint ping
    console.log('🔍 Test 1: Direct auth endpoint ping...');
    try {
      const pingResponse = await Promise.race([
        fetch(`${supabaseUrl}/auth/v1/settings`, {
          method: 'GET',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`
          }
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Ping timeout')), 3000))
      ]);
      
      if (pingResponse.ok) {
        console.log('✅ Auth endpoint ping successful');
      } else {
        console.log('❌ Auth endpoint ping failed:', pingResponse.status);
      }
    } catch (pingError) {
      console.log('❌ Auth endpoint ping error:', pingError.message);
    }
    
    // Test 2: Session check
    console.log('🔍 Test 2: Session check...');
    try {
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Session check timeout')), 3000);
      });
      
      const { data, error } = await Promise.race([sessionPromise, timeoutPromise]);
      
      if (error) {
        console.log('❌ Session check error:', error);
      } else {
        console.log('✅ Session check successful');
      }
    } catch (sessionError) {
      console.log('❌ Session check failed:', sessionError.message);
    }
    
    // Test 3: Auth settings check
    console.log('🔍 Test 3: Auth settings check...');
    try {
      const settingsResponse = await Promise.race([
        fetch(`${supabaseUrl}/auth/v1/settings`, {
          headers: {
            'apikey': supabaseKey
          }
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Settings timeout')), 3000))
      ]);
      
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        console.log('✅ Auth settings retrieved:', settings);
        return { success: true, settings };
      } else {
        console.log('❌ Auth settings failed:', settingsResponse.status);
      }
    } catch (settingsError) {
      console.log('❌ Auth settings error:', settingsError.message);
    }
    
    return { success: false, error: 'All auth endpoint tests failed' };
    
  } catch (error) {
    console.error('❌ Auth endpoint test error:', error);
    return { success: false, error: error.message };
  }
};

// Test login with dummy credentials using direct method
export const testLogin = async () => {
  try {
    console.log('🔍 Testing login with direct auth method...');
    
    const testResponse = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`
      },
      body: JSON.stringify({
        email: 'test@nonexistent.com',
        password: 'wrongpassword'
      })
    });
    
    console.log('📊 Test login response status:', testResponse.status);
    
    if (testResponse.status === 400) {
      const errorData = await testResponse.json();
      console.log('📊 Test login error data:', errorData);
      
      if (errorData.msg?.includes('Invalid login credentials')) {
        console.log('✅ Auth system is working (expected invalid credentials error)');
        return { success: true, message: 'Auth system is working properly' };
      }
    }
    
    if (testResponse.ok) {
      console.log('❌ Unexpected success with dummy credentials');
      return { success: false, error: 'Security issue: dummy credentials worked' };
    }
    
    return { success: false, error: 'Unexpected response from auth system' };
    
  } catch (error) {
    console.error('❌ Test login error:', error);
    return { success: false, error: error.message };
  }
};

// Emergency fallback login (if all else fails)
export const emergencyLogin = async (email, password) => {
  try {
    console.log('🚨 Emergency login attempt for:', email);
    
    // Try the REST API directly
    const response = await fetch(`${supabaseUrl}/rest/v1/rpc/authenticate_user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`
      },
      body: JSON.stringify({
        user_email: email,
        user_password: password
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Emergency login successful');
      return { success: true, data };
    } else {
      console.log('❌ Emergency login failed');
      return { success: false, error: 'Emergency login failed' };
    }
    
  } catch (error) {
    console.error('❌ Emergency login error:', error);
    return { success: false, error: error.message };
  }
};

// ===== ENHANCED AUTHENTICATION FUNCTIONS =====

// Enhanced signIn function that bypasses the timeout issue
export const signIn = async (email, password) => {
  const startTime = Date.now();
  
  try {
    console.log('🔄 Starting Supabase auth sign in for:', email);
    
    // Use Supabase's built-in auth instead of direct API calls
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password
    });
    
    if (error) {
      console.error('❌ Supabase auth sign in failed:', error);
      
      let userMessage = error.message;
      
      // Handle specific error messages
      if (error.message?.includes('Invalid login credentials')) {
        userMessage = 'Invalid email or password. Please check your credentials.';
      } else if (error.message?.includes('Email not confirmed')) {
        userMessage = 'Please check your email and confirm your account first.';
      } else if (error.message?.includes('Too many requests')) {
        userMessage = 'Too many login attempts. Please wait a moment and try again.';
      }
      
      return { success: false, error: userMessage };
    }
    
    console.log('✅ Supabase auth successful:', data.user.email);
    
    const duration = Date.now() - startTime;
    console.log(`✅ Sign in successful via Supabase auth (${duration}ms)`);
    
    return { success: true, user: data.user, session: data.session };
    
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`❌ Sign in failed after ${duration}ms:`, error);
    
    let userMessage = error.message;
    
    if (error.message?.includes('timeout')) {
      userMessage = 'Login is taking too long. Please try again.';
    } else if (error.message?.includes('fetch')) {
      userMessage = 'Unable to connect to authentication service. Please check your internet connection.';
    } else if (error.message?.includes('Network')) {
      userMessage = 'Network error. Please try again.';
    }
    
    return { success: false, error: userMessage };
  }
};

export const signUp = async (email, password, userData = {}) => {
  try {
    console.log('🔄 Supabase signUp: Starting for', email);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: userData.username || email.split('@')[0],
          role: userData.role || 'patient',
          patient_id: userData.patientId,
          notes: userData.notes,
          created_by: userData.createdBy,
          ...userData
        }
      }
    });

    if (error) {
      console.error('❌ Supabase signUp: Error:', error);
      throw error;
    }
    
    console.log('✅ Supabase signUp: Success for', email);
    return { success: true, user: data.user };
  } catch (error) {
    console.error('❌ Supabase signUp: Catch error:', error);
    return { success: false, error: error.message };
  }
};

export const signOut = async () => {
  try {
    console.log('🔄 Supabase signOut: Starting');
    
    // Clear localStorage first
    localStorage.removeItem('supabase_auth_token');
    localStorage.removeItem('supabase_refresh_token');
    localStorage.removeItem('supabase_user');
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('❌ Supabase signOut: Error:', error);
      throw error;
    }
    
    console.log('✅ Supabase signOut: Success');
    return { success: true };
  } catch (error) {
    console.error('❌ Supabase signOut: Catch error:', error);
    return { success: false, error: error.message };
  }
};

export const getCurrentUser = async () => {
  try {
    console.log('🔄 Getting current user from stored session');
    
    // First try to get from localStorage
    const storedUser = localStorage.getItem('supabase_user');
    const storedToken = localStorage.getItem('supabase_auth_token');
    
    if (storedUser && storedToken) {
      console.log('✅ Found stored user session');
      return JSON.parse(storedUser);
    }
    
    // If no stored session, try the Supabase client (with timeout)
    const { data: { user }, error } = await Promise.race([
      supabase.auth.getUser(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('GetUser timeout')), 3000)
      )
    ]);
    
    if (error) {
      console.warn('⚠️ Supabase getCurrentUser error:', error);
      return null;
    }
    
    console.log('✅ Got user from Supabase client');
    return user;
    
  } catch (error) {
    console.error('❌ getCurrentUser error:', error);
    return null;
  }
};

export const getCurrentSession = async () => {
  try {
    console.log('🔄 Supabase getCurrentSession: Starting');
    
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('❌ Supabase getCurrentSession: Error:', error);
      throw error;
    }
    
    console.log('✅ Supabase getCurrentSession: Result:', session?.user?.email || 'No session');
    return session;
  } catch (error) {
    console.error('❌ Supabase getCurrentSession: Catch error:', error);
    return null;
  }
};

export const onAuthStateChange = (callback) => {
  console.log('🔄 Supabase onAuthStateChange: Setting up listener');
  return supabase.auth.onAuthStateChange(callback);
};

export const resetPassword = async (email) => {
  try {
    console.log('🔄 Supabase resetPassword: Starting for', email);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) {
      console.error('❌ Supabase resetPassword: Error:', error);
      throw error;
    }
    
    console.log('✅ Supabase resetPassword: Success');
    return { success: true };
  } catch (error) {
    console.error('❌ Supabase resetPassword: Catch error:', error);
    return { success: false, error: error.message };
  }
};

// ===== ADMIN FUNCTIONS =====

/**
 * Check if a user has admin/researcher role
 * @param {string} userId - The user's ID
 * @returns {boolean} - True if user is admin/researcher
 */
export const isUserAdmin = async (userId) => {
  try {
    console.log('🔍 Checking admin status for user:', userId);
    
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error('❌ Error checking admin status:', error);
      return false;
    }
    
    const isAdmin = data?.role === 'researcher' || data?.role === 'admin';
    console.log(`✅ Admin check result: ${isAdmin}`);
    
    return isAdmin;
    
  } catch (error) {
    console.error('❌ Admin check error:', error);
    return false;
  }
};

/**
 * Check if current logged-in user is admin
 * @returns {Object} - {isAdmin: boolean, user: object}
 */
export const checkCurrentUserAdmin = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return { isAdmin: false, user: null };
    }
    
    const isAdmin = await isUserAdmin(user.id);
    
    return { isAdmin, user };
    
  } catch (error) {
    console.error('❌ Error checking current user admin:', error);
    return { isAdmin: false, user: null };
  }
};

/**
 * Verify admin access for researcher dashboard
 * @param {string} userId - The user's ID
 * @returns {Object} - {hasAccess: boolean, message: string}
 */
export const verifyAdminAccess = async (userId) => {
  try {
    const isAdmin = await isUserAdmin(userId);
    
    if (!isAdmin) {
      return {
        hasAccess: false,
        message: 'Access denied. This area is restricted to researchers only.'
      };
    }
    
    return {
      hasAccess: true,
      message: 'Access granted'
    };
    
  } catch (error) {
    console.error('❌ Error verifying admin access:', error);
    return {
      hasAccess: false,
      message: 'Error verifying access. Please try again.'
    };
  }
};

// ===== USER PROFILE FUNCTIONS =====
// Replace your getUserProfile function with this version
export const getUserProfile = async (userId) => {
  try {
    console.log('🔄 Supabase getUserProfile: Starting for', userId);
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error('❌ Supabase getUserProfile: Error:', error);
      
      if (error.code === 'PGRST116') {
        console.log('ℹ️ No profile found for user:', userId);
        return null;
      }
      
      throw error;
    }
    
    if (data) {
      console.log('✅ Supabase getUserProfile: Success for', data.email);
      return data;
    } else {
      console.log('ℹ️ No profile data found for user:', userId);
      return null;
    }
    
  } catch (error) {
    console.error('❌ Supabase getUserProfile: Catch error:', error);
    return null;
  }
};

// Replace your saveUserProfile function with this version
export const saveUserProfile = async (user) => {
  try {
    console.log('🔄 Supabase saveUserProfile: Starting for', user.email);
    
    // Get or create UUID mapping for Firebase user
    let supabaseUserId;
    
    // First check if mapping exists
    const { data: existingMapping, error: mappingError } = await supabase
      .from('id_mapping')
      .select('supabase_uuid')
      .eq('firebase_id', user.uid)
      .single();
    
    if (existingMapping) {
      supabaseUserId = existingMapping.supabase_uuid;
      console.log('✅ Found existing UUID mapping:', supabaseUserId);
    } else {
      // Create new mapping
      const { data: newMapping, error: newMappingError } = await supabase
        .from('id_mapping')
        .insert({ firebase_id: user.uid })
        .select('supabase_uuid')
        .single();
      
      if (newMappingError) {
        console.error('❌ Error creating UUID mapping:', newMappingError);
        throw newMappingError;
      }
      
      supabaseUserId = newMapping.supabase_uuid;
      console.log('✅ Created new UUID mapping:', supabaseUserId);
    }
    
    const username = user.displayName || user.email?.split('@')[0] || 'User';
    const profileData = {
      id: supabaseUserId, // Use the mapped UUID
      email: user.email,
      username: username,
      role: user.customClaims?.role || 'patient',
      patient_id: user.customClaims?.patient_id,
      notes: user.customClaims?.notes,
      created_by: user.customClaims?.created_by,
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('users')
      .upsert(profileData, { onConflict: 'id' })
      .select()
      .single();
    
    if (error) {
      console.error('❌ Supabase saveUserProfile: Error:', error);
      throw error;
    }
    
    console.log('✅ Supabase saveUserProfile: Success');
    return data;
    
  } catch (error) {
    console.error('❌ Supabase saveUserProfile: Catch error:', error);
    return null;
  }
};

// Add this function to test database connectivity
window.testDB = async () => {
  try {
    console.log('🔍 Testing database connection...');
    
    // Test user query
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', 'dd5be8e0-d7ba-4868-aeaf-0bec3a8b4065');
    
    console.log('User query result:', { userData, userError });
    
    // Test sessions query  
    const { data: sessionsData, error: sessionsError } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', 'dd5be8e0-d7ba-4868-aeaf-0bec3a8b4065');
    
    console.log('Sessions query result:', { sessionsData, sessionsError });
    
    return { userData, sessionsData, userError, sessionsError };
  } catch (error) {
    console.error('Database test error:', error);
    return { error };
  }
};

// ===== SESSION FUNCTIONS =====

// Default session status helper
const getDefaultSessionStatus = (sessionNumber) => ({
  isSessionCompleted: false,
  sessionStarted: false, // CHANGED: Don't auto-start any session
  isUnlocked: sessionNumber <= 1, // Session 0 and 1 are unlocked by default
  videoWatched: false,
  quizCompleted: false,
  videoProgress: 0,
  lastVideoPosition: 0,
  nextSessionUnlockTime: null,
  painLevel: null,
  quizScore: null,
  preAssessmentLinkClicked: false,
  preAssessmentCompleted: false,
 // postAssessmentLinkClicked: false,
 // postAssessmentCompleted: false,
  sessionFullyCompleted: false
});

// Default sessions helper
const getDefaultSessions = () => [
  { id: "session1", number: 1, title: "Understanding Chronic Orofacial Pain", subtitle: "Introduction to the fundamentals", estimatedTime: "20 min", unlocked: true, completed: false },
  { id: "session2", number: 2, title: "Identifying Triggers & Managing Flare-Ups", subtitle: "Learn to identify and manage triggers", estimatedTime: "25 min", unlocked: false, completed: false },
  { id: "session3", number: 3, title: "Evidence-Based Pain Management", subtitle: "Explore evidence-based strategies", estimatedTime: "30 min", unlocked: false, completed: false },
  { id: "session4", number: 4, title: "Lifestyle Adjustments for Relief", subtitle: "Adjust your lifestyle for long-term relief", estimatedTime: "28 min", unlocked: false, completed: false },
  { id: "session5", number: 5, title: "Building a Sustainable Plan", subtitle: "Create your personalized management plan", estimatedTime: "35 min", unlocked: false, completed: false }
];

// Separate function to create a user session
const createUserSession = async (userId, sessionNumber) => {
  try {
    console.log(`🔄 Creating session ${sessionNumber} for user ${userId}`);
    
    const sessionData = {
      user_id: userId,
      session_number: sessionNumber,
      session_started: false, // CHANGED: Don't auto-start any session
      session_completed: false,
      video_watched: false,
      quiz_completed: false,
      video_progress_percentage: 0,
      last_video_position: 0,
      pain_level: null,
      quiz_score: null,
      pre_assessment_link_clicked: false,
      pre_assessment_completed: false,
     // post_assessment_link_clicked: false,
     // post_assessment_completed: false,
      session_fully_completed: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('user_sessions')
      .insert(sessionData)
      .select()
      .single();
    
    if (error) {
      const errorInfo = handleSupabaseError(error, 'createUserSession');
      console.error('❌ Error creating session:', errorInfo);
      return false;
    }
    
    console.log('✅ Session created successfully:', data);
    return true;
    
  } catch (error) {
    console.error('❌ Unexpected error creating session:', error);
    return false;
  }
};

// FIXED: Enhanced session status fetch with corrected SQL and RedCap support
export const fetchSessionStatusFast = async (userId, sessionNumber) => {
  try {
    console.log(`🚀 Fetching session ${sessionNumber} for user ${userId}`);

    const { data: session, error } = await supabase
      .from('user_sessions')
      .select(
        `
        session_completed,
        session_started,
        video_watched,
        quiz_completed,
        video_progress_percentage,
        last_video_position,
        next_session_unlock_time,
        pain_level,
        quiz_score,
        session_number,
        user_id,
        pain_level_timestamp,
        video_watch_time_seconds,
        pre_assessment_link_clicked,
        pre_assessment_clicked_timestamp,
        pre_assessment_completed,
        pre_assessment_completion_timestamp,
        session_fully_completed
      `,
      )
      .eq('user_id', userId)
      .eq('session_number', sessionNumber)
      .maybeSingle();

    if (error) {
      console.error(`❌ Error fetching session ${sessionNumber}:`, error);
      return getDefaultSessionStatus(sessionNumber);
    }

    if (!session) {
      console.log(`📝 Session ${sessionNumber} not found, creating...`);
      const created = await createUserSession(userId, sessionNumber);
      if (created) {
        return getDefaultSessionStatus(sessionNumber);
      }
      return getDefaultSessionStatus(sessionNumber);
    }

    // Use centralized access rules so route guards and page UI agree.
    let isUnlocked = false;
    try {
      isUnlocked = await canAccessSession(userId, sessionNumber);
    } catch (unlockError) {
      console.warn('⚠️ Error checking unlock status:', unlockError);
      isUnlocked = false;
    }

    // ✅ UPDATED: Better detection of session start with RedCap assessment
    // Session is considered started if user has completed pre-assessment OR made ANY progress
    const hasAnyProgress =
      session.video_progress_percentage > 0 ||
      session.video_watch_time_seconds > 0 ||
      session.last_video_position > 0 ||
      session.pain_level !== null ||
      session.pain_level_timestamp !== null;

    // NEW: Check for pre-assessment completion as the primary start indicator
    const effectiveSessionStarted =
      session.pre_assessment_completed ||
      session.session_started ||
      hasAnyProgress;

    const status = {
      isSessionCompleted:
        session.session_fully_completed || session.session_completed || false,
      sessionStarted: effectiveSessionStarted, // ✅ This is the key fix
      isUnlocked,
      videoWatched: session.video_watched || false,
      videoCompleted: session.video_watched || false,
      quizCompleted: session.quiz_completed || false,
      videoProgress: session.video_progress_percentage || 0,
      lastVideoPosition: session.last_video_position || 0,
      nextSessionUnlockTime: session.next_session_unlock_time || null,
      painLevel: session.pain_level || null,
      quizScore: session.quiz_score || null,
      // RedCap assessment states
      preAssessmentLinkClicked: session.pre_assessment_link_clicked || false,
      preAssessmentCompleted: session.pre_assessment_completed || false,
      // postAssessmentLinkClicked: session.post_assessment_link_clicked || false,
      // postAssessmentCompleted: session.post_assessment_completed || false,
      sessionFullyCompleted: session.session_fully_completed || false,
    };

    console.log(`✅ Session ${sessionNumber} status loaded:`, status);
    return status;
  } catch (error) {
    console.error(`❌ Unexpected error in fetchSessionStatusFast:`, error);
    return getDefaultSessionStatus(sessionNumber);
  }
};

// Enhanced session progress update with better error handling
export const updateSessionProgress = async (userId, sessionNumber, progressData) => {
  try {
    // Reduced logging - only log important updates
    const isImportantUpdate = progressData.session_completed || 
                             progressData.video_completed || 
                             progressData.quiz_completed ||
                             progressData.pre_assessment_completed;
    
    if (isImportantUpdate) {
      console.log(`🔄 Important update for session ${sessionNumber}:`, progressData);
    }
    
    // First, check if session exists and handle duplicates
    const { data: existingSessions, error: checkError } = await supabase
      .from('user_sessions')
      .select('id, session_number')
      .eq('user_id', userId)
      .eq('session_number', sessionNumber);
    
    if (checkError) {
      console.error('❌ Error checking existing sessions:', checkError);
      return null;
    }
    
    if (!existingSessions || existingSessions.length === 0) {
      console.log('📝 Session not found, creating first...');
      const created = await createUserSession(userId, sessionNumber);
      if (!created) {
        console.error('❌ Failed to create session');
        return null;
      }
    } else if (existingSessions.length > 1) {
      console.warn('⚠️ Multiple sessions found, cleaning up duplicates...');
      // Keep the first one, delete the rest
      const toDelete = existingSessions.slice(1);
      for (const duplicate of toDelete) {
        await supabase
          .from('user_sessions')
          .delete()
          .eq('id', duplicate.id);
      }
      console.log(`✅ Cleaned up ${toDelete.length} duplicate sessions`);
    }
    
    // Prepare update data with proper types
    const updateData = {
      ...progressData,
      updated_at: new Date().toISOString()
    };
    
    // Remove any undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });
    
    // Use the first session's ID for update
    const sessionId = existingSessions[0]?.id;
    
    let result;
    if (sessionId) {
      // Update by ID to avoid multiple row issues
      const { data, error } = await supabase
        .from('user_sessions')
        .update(updateData)
        .eq('id', sessionId)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error updating session by ID:', error);
        return await alternativeUpdateApproach(userId, sessionNumber, updateData);
      }
      
      result = data;
    } else {
      // Fallback to user_id and session_number
      const { data, error } = await supabase
        .from('user_sessions')
        .update(updateData)
        .eq('user_id', userId)
        .eq('session_number', sessionNumber)
        .select()
        .maybeSingle();
      
      if (error) {
        console.error('❌ Error updating session by user_id/session_number:', error);
        return await alternativeUpdateApproach(userId, sessionNumber, updateData);
      }
      
      result = data;
    }
    
    if (!result) {
      console.error('❌ No result from update operation');
      return null;
    }
    
    if (isImportantUpdate) {
      console.log(`✅ Session ${sessionNumber} progress updated successfully`);
    }
    return result;
    
  } catch (error) {
    console.error(`❌ Unexpected error updating session progress:`, error);
    return null;
  }
};

// Alternative update approach for 406 errors
const alternativeUpdateApproach = async (userId, sessionNumber, updateData) => {
  try {
    console.log('🔄 Trying alternative update approach...');
    
    // Use upsert instead of update
    const upsertData = {
      user_id: userId,
      session_number: sessionNumber,
      ...updateData
    };
    
    const { data, error } = await supabase
      .from('user_sessions')
      .upsert(upsertData, {
        onConflict: 'user_id,session_number'
      })
      .select()
      .single();
    
    if (error) {
      console.error('❌ Alternative update also failed:', error);
      return null;
    }
    
    console.log('✅ Alternative update succeeded');
    return data;
    
  } catch (error) {
    console.error('❌ Alternative update error:', error);
    return null;
  }
};

export const initializeUserSessions = async (userId) => {
  if (!userId) {
    console.error("❌ initializeUserSessions: No userId provided");
    return false;
  }

  try {
    console.log('🔄 Initializing sessions for user:', userId);
    
    // Check existing sessions with better error handling
    const { data: existingSessions, error: fetchError } = await supabase
      .from('user_sessions')
      .select('session_number, id')
      .eq('user_id', userId)
      .order('session_number');
    
    if (fetchError) {
      console.error('❌ Error checking existing sessions:', fetchError);
      return false;
    }
    
    console.log('📊 Found existing sessions:', existingSessions?.length || 0);
    
    const existingNumbers = existingSessions?.map(s => s.session_number) || [];
    const sessionsToCreate = [];
    
    // FIXED: Create sessions 0-5, but DON'T auto-start any of them
    for (let i = 0; i <= 5; i++) {
      if (!existingNumbers.includes(i)) {
        sessionsToCreate.push({
          user_id: userId,
          session_number: i,
          session_started: false, // CHANGED: Don't auto-start ANY session including session 0
          session_completed: false,
          video_watched: false,
          video_completed: false,
          quiz_completed: false,
          video_progress_percentage: 0,
          video_watch_time_seconds: 0,
          last_video_position: 0,
          pain_level: null,
          quiz_score: null,
          quiz_attempts: 0,
          pre_assessment_link_clicked: false,
          pre_assessment_completed: false,
       //   post_assessment_link_clicked: false,
       //   post_assessment_completed: false,
          session_fully_completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    }
    
    if (sessionsToCreate.length > 0) {
      console.log(`🔄 Creating ${sessionsToCreate.length} missing sessions:`, sessionsToCreate.map(s => s.session_number));
      
      // Use upsert with onConflict to handle any race conditions
      const { data, error } = await supabase
        .from('user_sessions')
        .upsert(sessionsToCreate, {
          onConflict: 'user_id,session_number',
          ignoreDuplicates: true  // This will ignore duplicates instead of erroring
        })
        .select();
      
      if (error) {
        console.error('❌ Error creating sessions:', error);
        
        // Try creating sessions one by one if batch fails
        console.log('🔄 Batch creation failed, trying individual creation...');
        return await createSessionsIndividually(userId, sessionsToCreate);
      }
      
      console.log('✅ Sessions created successfully:', data?.length || 0);
    } else {
      console.log('✅ All sessions already exist');
    }
    
    return true;
    
  } catch (error) {
    console.error('❌ Unexpected error in session initialization:', error);
    return false;
  }
};
// UPDATED: Helper function to create sessions individually (now includes session 0)
const createSessionsIndividually = async (userId, sessionsToCreate) => {
  console.log('🔄 Creating sessions individually for user:', userId);
  
  let successCount = 0;
  
  for (const sessionData of sessionsToCreate) {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .insert(sessionData)
        .select()
        .single();
      
      if (error) {
        // If it's a duplicate key error, that's okay
        if (error.code === '23505') {
          console.log(`✅ Session ${sessionData.session_number} already exists (duplicate key)`);
          successCount++;
        } else {
          console.error(`❌ Error creating session ${sessionData.session_number}:`, error);
        }
      } else {
        console.log(`✅ Session ${sessionData.session_number} created successfully`);
        successCount++;
      }
      
    } catch (error) {
      console.error(`❌ Unexpected error creating session ${sessionData.session_number}:`, error);
    }
  }
  
  console.log(`✅ Created ${successCount}/${sessionsToCreate.length} sessions individually`);
  return successCount > 0;
};



// FIXED: Enhanced start session function with proper timestamp
export const startSession = async (userId, sessionNumber) => {
  try {
    console.log(`🚀 Starting session ${sessionNumber}`);
    
    const updateData = {
      session_started: true,
      session_start_time: Date.now(), // Use Date.now() for bigint
      updated_at: new Date().toISOString() // Use ISO string for timestamp
    };
    
    const result = await updateSessionProgress(userId, sessionNumber, updateData);
    
    if (result) {
      console.log(`✅ Session ${sessionNumber} started successfully`);
      return true;
    } else {
      console.error(`❌ Failed to start session ${sessionNumber}`);
      return false;
    }
    
  } catch (error) {
    console.error(`❌ Start session error:`, error);
    return false;
  }
};

// Enhanced pain level saving
export const savePainLevel = async (userId, sessionId, painLevel) => {
  try {
    console.log(`🔄 Saving pain level ${painLevel} for session ${sessionId}`);
    
    const sessionNumber = parseInt(sessionId.replace('session', ''));
    
    const updateData = {
      pain_level: painLevel,
      pain_level_timestamp: Date.now(), // Use Date.now() for bigint
      pain_level_date: new Date().toISOString().split('T')[0], // Keep date as string
      updated_at: new Date().toISOString()
    };
    
    const result = await updateSessionProgress(userId, sessionNumber, updateData);
    
    if (result) {
      console.log(`✅ Pain level saved successfully`);
      return true;
    } else {
      console.error(`❌ Failed to save pain level`);
      return false;
    }
    
  } catch (error) {
    console.error(`❌ Save pain level error:`, error);
    return false;
  }
};

// Enhanced dashboard data fetching
export const fetchDashboardDataFast = async (userId) => {
  try {
    console.log('🚀 Fast dashboard loading for user:', userId);
    
    // Initialize sessions first
    await initializeUserSessions(userId);
    
    const { data: sessions, error } = await supabase
      .from('user_sessions')
      .select('session_number, session_completed, session_fully_completed, video_watched, quiz_completed, next_session_unlock_time, pain_level, quiz_score, pre_assessment_completed')
      .eq('user_id', userId)
      .order('session_number');
    
    if (error) {
      const errorInfo = handleSupabaseError(error, 'fetchDashboardDataFast');
      console.error('❌ Dashboard query error:', errorInfo);
      return getDefaultSessions();
    }
    
    if (!sessions || sessions.length === 0) {
      console.log('🔄 No sessions found, returning defaults');
      return getDefaultSessions();
    }
    
    // Transform to dashboard format
    const sessionMetadata = [
      { id: "session1", number: 1, title: "Understanding Chronic Orofacial Pain", subtitle: "Introduction to the fundamentals", estimatedTime: "20 min" },
      { id: "session2", number: 2, title: "Identifying Triggers & Managing Flare-Ups", subtitle: "Learn to identify and manage triggers", estimatedTime: "25 min" },
      { id: "session3", number: 3, title: "Evidence-Based Pain Management", subtitle: "Explore evidence-based strategies", estimatedTime: "30 min" },
      { id: "session4", number: 4, title: "Lifestyle Adjustments for Relief", subtitle: "Adjust your lifestyle for long-term relief", estimatedTime: "28 min" },
      { id: "session5", number: 5, title: "Building a Sustainable Plan", subtitle: "Create your personalized management plan", estimatedTime: "35 min" }
    ];
    
    const result = sessionMetadata.map(metadata => {
      const session = sessions.find(s => s.session_number === metadata.number);
      
      // Fast unlock check
      let isUnlocked = false;
      if (metadata.number === 1) {
        isUnlocked = true;
      } else {
        const previousSession = sessions.find(s => s.session_number === metadata.number - 1);
        if (previousSession) {
          isUnlocked = previousSession.session_fully_completed || 
                      previousSession.session_completed || 
                      (previousSession.video_watched && previousSession.quiz_completed);
        }
      }
      
      return {
        id: metadata.id,
        number: metadata.number,
        title: metadata.title,
        subtitle: metadata.subtitle,
        estimatedTime: metadata.estimatedTime,
        completed: session?.session_fully_completed || session?.session_completed || false,
        unlocked: isUnlocked,
        nextSessionUnlockTime: session?.next_session_unlock_time || null,
        painLevel: session?.pain_level || null,
        quizScore: session?.quiz_score || null
      };
    });
    
    console.log('✅ Fast dashboard data loaded:', result.length, 'sessions');
    return result;
    
  } catch (error) {
    console.error('❌ Fast dashboard loading error:', error);
    return getDefaultSessions();
  }
};

// Test database connection
export const testDatabaseConnection = async () => {
  try {
    console.log('🔍 Testing database connection...');
    
    const { data, error } = await supabase
      .from('user_sessions')
      .select('count')
      .limit(1);
    
    if (error) {
      const errorInfo = handleSupabaseError(error, 'testDatabaseConnection');
      console.error('❌ Database connection test failed:', errorInfo);
      return false;
    }
    
    console.log('✅ Database connection test passed');
    return true;
    
  } catch (error) {
    console.error('❌ Database connection test error:', error);
    return false;
  }
};

// Complete session
export const completeSession = async (userId, sessionNumber) => {
  try {
    console.log(`🔄 Completing session ${sessionNumber}`);
    
    const updateData = {
      session_completed: true,
      session_completion_time: new Date().toISOString(),
      completion_timestamp: Date.now(), // Use Date.now() for bigint
      completion_date: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString()
    };
    
    const result = await updateSessionProgress(userId, sessionNumber, updateData);
    
    if (result) {
      console.log(`✅ Session ${sessionNumber} completed`);
      return result;
    } else {
      console.error(`❌ Failed to complete session ${sessionNumber}`);
      return null;
    }
    
  } catch (error) {
    console.error(`❌ Complete session error:`, error);
    return null;
  }
};

// Enhanced quiz completion
export const completeQuizWithAnalytics = async (userId, sessionNumber, quizData) => {
  try {
    console.log(`🔄 Completing quiz for session ${sessionNumber}`);
    
    const updateData = {
      quiz_completed: true,
      quiz_score: quizData.score,
      quiz_answers: quizData.answers,
      quiz_attempts: quizData.attempts || 1,
      quiz_completion_time: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const result = await updateSessionProgress(userId, sessionNumber, updateData);
    
    if (result) {
      console.log('✅ Quiz completed with analytics');
      
      // Check if session should be marked as completed
      if (result.video_watched && result.quiz_completed) {
        await markSessionCompleted(userId, sessionNumber);
      }
      
      return result;
    } else {
      console.error('❌ Failed to complete quiz');
      return null;
    }
    
  } catch (error) {
    console.error('❌ Quiz completion error:', error);
    return null;
  }
};

export const markSessionCompleted = async (userId, sessionNumber) => {
  try {
    console.log(`🔄 Marking session ${sessionNumber} as completed`);
    
    // Update current session
    const updateData = {
      session_completed: true,
      completion_timestamp: Date.now(),
      completion_date: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString()
    };
    
    const result = await updateSessionProgress(userId, sessionNumber, updateData);
    
    if (result) {
      // Check if there's a scheduled unlock time for the next session
      if (sessionNumber < 5) {
        const nextSessionNumber = sessionNumber + 1;
        
        // FIXED: Check participant schedule first
        const { data: schedule, error: scheduleError } = await supabase
          .from('participant_session_schedule')
          .select(`session_${nextSessionNumber}_unlock_time`)
          .eq('user_id', userId)
          .single();
        
        if (!scheduleError && schedule) {
          const scheduledUnlockTime = schedule[`session_${nextSessionNumber}_unlock_time`];
          
          if (scheduledUnlockTime) {
            // Use the scheduled unlock time
            const scheduledTime = new Date(scheduledUnlockTime).getTime();
            console.log(`✅ Using scheduled unlock time for session ${nextSessionNumber}: ${new Date(scheduledTime)}`);
            
            await updateSessionProgress(userId, nextSessionNumber, {
              session_started: false,
              next_session_unlock_time: scheduledTime,
              updated_at: new Date().toISOString()
            });
            
            console.log(`✅ Next session ${nextSessionNumber} scheduled according to participant schedule`);
            return result;
          }
        }
        
        // Fallback: If no schedule found, use 2-minute delay (original logic)
        console.log(`⚠️ No schedule found for user ${userId}, using default 2-minute delay`);
        const unlockTime = Date.now() + (2 * 60 * 1000);
        
        await updateSessionProgress(userId, nextSessionNumber, {
          session_started: false,
          next_session_unlock_time: unlockTime,
          updated_at: new Date().toISOString()
        });
        
        console.log(`✅ Next session ${nextSessionNumber} scheduled to unlock in 2 minutes (default)`);
      }
      
      console.log('✅ Session marked as completed');
      return result;
    } else {
      console.error('❌ Failed to mark session as completed');
      return null;
    }
    
  } catch (error) {
    console.error('❌ Session completion error:', error);
    return null;
  }
};

// FIXED: Updated session unlock logic to check scheduled times
export const canAccessSession = async (userId, sessionNumber) => {
  try {
    console.log(`🔄 Checking access for session ${sessionNumber}`);

    // Session 0 is always accessible.
    if (sessionNumber === 0) {
      return true;
    }

    // Manual override has top priority.
    const { data: manualOverride, error: overrideError } = await supabase
      .from('manual_session_overrides')
      .select('override_status')
      .eq('user_id', userId)
      .eq('session_number', sessionNumber)
      .maybeSingle();

    if (!overrideError && manualOverride?.override_status) {
      const overrideStatus = String(
        manualOverride.override_status,
      ).toLowerCase();
      if (['unlocked', 'complete', 'completed'].includes(overrideStatus)) {
        console.log(`✅ Session ${sessionNumber} manually unlocked`);
        return true;
      }
      if (['locked', 'lock'].includes(overrideStatus)) {
        console.log(`🔒 Session ${sessionNumber} manually locked`);
        return false;
      }
    }

    // Session 1 unlocks after Session 0 completion OR (video_completed AND quiz_completed).
    if (sessionNumber === 1) {
      const { data: sessionZero, error: sessionZeroError } = await supabase
        .from('user_sessions')
        .select('session_completed, video_completed, quiz_completed')
        .eq('user_id', userId)
        .eq('session_number', 0)
        .maybeSingle();

      if (sessionZeroError) {
        console.error(
          '❌ Error checking session 0 completion:',
          sessionZeroError,
        );
        return false;
      }

      const canAccessSessionOne =
        sessionZero?.session_completed ||
        (sessionZero?.video_completed && sessionZero?.quiz_completed);
      console.log(`✅ Session 1 access: ${canAccessSessionOne}`);
      return !!canAccessSessionOne;
    }

    // Sessions 2-5: schedule gate controls unlock (with manual override priority handled above).
    if (sessionNumber > 5) {
      return false;
    }

    // Check if there's a scheduled unlock time
    const { data: schedule, error: scheduleError } = await supabase
      .from('participant_session_schedule')
      .select(`session_${sessionNumber}_unlock_time`)
      .eq('user_id', userId)
      .single();

    if (!scheduleError && schedule) {
      const scheduledUnlockTime =
        schedule[`session_${sessionNumber}_unlock_time`];

      if (scheduledUnlockTime) {
        const now = new Date();
        const unlockTime = new Date(scheduledUnlockTime);
        const isScheduledUnlocked = now >= unlockTime;

        console.log(
          `📅 Session ${sessionNumber} scheduled unlock: ${unlockTime}, current time: ${now}, unlocked: ${isScheduledUnlocked}`,
        );

        if (!isScheduledUnlocked) {
          return false; // Session is still time-locked
        }

        // Scheduled unlock time has passed.
        return true;
      }
    }

    // If no explicit schedule is configured, keep locked by default for sessions 2-5.
    console.log(
      `🔒 Session ${sessionNumber} has no eligible schedule unlock yet`,
    );
    return false;
  } catch (error) {
    console.error('❌ Session access check error:', error);
    return false;
  }
};

// Get next available session
export const getNextAvailableSession = async (userId) => {
  try {
    console.log('🔄 Getting next available session');
    
    const { data: sessions, error } = await supabase
      .from('user_sessions')
      .select('session_number, session_completed, session_fully_completed, video_watched, quiz_completed')
      .eq('user_id', userId)
      .order('session_number');
    
    if (error) {
      console.error('❌ Error getting next session:', error);
      return 1; // Default to session 1
    }
    
    // Find first incomplete session
    const nextSession = sessions.find(session => 
      !session.session_fully_completed && !session.session_completed
    );
    
    console.log('✅ Next available session:', nextSession?.session_number || 'None');
    return nextSession?.session_number || null;
    
  } catch (error) {
    console.error('❌ Next session error:', error);
    return 1; // Default to session 1
  }
};

// Get user analytics
export const getUserAnalytics = async (userId) => {
  try {
    console.log('🔄 Getting user analytics for:', userId);
    
    const { data: sessions, error } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', userId);
    
    if (error) {
      console.error('❌ Error getting analytics:', error);
      return {
        totalSessions: 0,
        completedSessions: 0,
        totalVideoWatchTime: 0,
        averagePainLevel: 0,
        quizScores: [],
        lastActivity: new Date().toISOString(),
        progressPercentage: 0
      };
    }
    
    // Calculate analytics
    const analytics = {
      totalSessions: sessions.length,
      completedSessions: sessions.filter(s => s.session_fully_completed || s.session_completed).length,
      totalVideoWatchTime: sessions.reduce((sum, s) => sum + (s.video_watch_time_seconds || 0), 0),
      averagePainLevel: sessions.filter(s => s.pain_level).reduce((sum, s, _, arr) => sum + s.pain_level / arr.length, 0),
      quizScores: sessions.filter(s => s.quiz_score).map(s => s.quiz_score),
      lastActivity: sessions.reduce((latest, s) => 
        new Date(s.updated_at) > new Date(latest) ? s.updated_at : latest, 
        sessions[0]?.updated_at || new Date().toISOString()
      ),
      progressPercentage: sessions.length > 0 ? 
        (sessions.filter(s => s.session_fully_completed || s.session_completed).length / sessions.length) * 100 : 0
    };
    
    console.log('✅ Analytics calculated:', analytics);
    return analytics;
    
  } catch (error) {
    console.error('❌ Analytics error:', error);
    return {
      totalSessions: 0,
      completedSessions: 0,
      totalVideoWatchTime: 0,
      averagePainLevel: 0,
      quizScores: [],
      lastActivity: new Date().toISOString(),
      progressPercentage: 0
    };
  }
};

// Get pain level history
export const getPainLevelHistory = async (userId) => {
  try {
    console.log('🔄 Getting pain level history for:', userId);
    
    const { data, error } = await supabase
      .from('user_sessions')
      .select('session_number, pain_level, pain_level_timestamp, pain_level_date')
      .eq('user_id', userId)
      .not('pain_level', 'is', null)
      .order('session_number');
    
    if (error) {
      console.error('❌ Error getting pain history:', error);
      return [];
    }
    
    console.log('✅ Pain history retrieved:', data?.length || 0);
    return data || [];
    
  } catch (error) {
    console.error('❌ Pain history error:', error);
    return [];
  }
};

// Subscribe to session updates
export const subscribeToSessionUpdates = (userId, callback) => {
  console.log('🔄 Setting up session updates subscription');
  
  const subscription = supabase
    .channel(`user-sessions-${userId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'user_sessions',
      filter: `user_id=eq.${userId}`
    }, callback)
    .subscribe();
  
  return subscription;
};

// Unsubscribe from session updates
export const unsubscribeFromSessionUpdates = (subscription) => {
  console.log('🔄 Unsubscribing from session updates');
  supabase.removeChannel(subscription);
};

// Debug session state
export const debugSessionState = async (userId, sessionNumber) => {
  try {
    console.log(`🔍 Debug session ${sessionNumber} for user ${userId}`);
    
    const { data, error } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('session_number', sessionNumber)
      .maybeSingle();
    
    if (error) {
      console.error(`❌ Debug session error:`, error);
      return null;
    }
    
    console.log(`📊 Session ${sessionNumber} debug data:`, data);
    return data;
    
  } catch (error) {
    console.error(`❌ Debug session error:`, error);
    return null;
  }
};

// Simplified batch session updates
export const batchUpdateSession = async (userId, sessionNumber, updates) => {
  try {
    console.log(`🔄 Batch updating session ${sessionNumber}:`, updates);
    
    const result = await updateSessionProgress(userId, sessionNumber, updates);
    
    if (result) {
      console.log('✅ Batch update completed');
      return result;
    } else {
      console.error('❌ Batch update failed');
      return null;
    }
    
  } catch (error) {
    console.error('❌ Batch update error:', error);
    return null;
  }
};

// Legacy compatibility functions
export const saveQuizResult = async (userId, sessionNumber, quizData) => {
  return await updateSessionProgress(userId, sessionNumber, {
    quiz_completed: true,
    quiz_score: quizData.score,
    quiz_answers: quizData.answers,
    quiz_completion_time: new Date().toISOString()
  });
};

// ===== PRACTICE LOG FUNCTIONS =====

// Save practice log entry
export const savePracticeLog = async (userId, practiceData) => {
  try {
    console.log('🔄 Saving practice log for user:', userId);
    
    const logData = {
      user_id: userId,
      practice_date: practiceData.date,
      skill: practiceData.skill,
      minutes: practiceData.minutes,
      practice_type: practiceData.practiceType,
      session_number: practiceData.sessionNumber,
      timestamp: Date.now(), // Using bigint timestamp like your schema
      created_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('practice_logs')
      .insert(logData)
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error saving practice log:', error);
      throw error;
    }
    
    console.log('✅ Practice log saved successfully');
    return data;
    
  } catch (error) {
    console.error('❌ Save practice log error:', error);
    throw error;
  }
};

// Get user practice log
export const getUserPracticeLog = async (userId) => {
  try {
    console.log('🔄 Fetching practice log for user:', userId);
    
    const { data, error } = await supabase
      .from('practice_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('❌ Error fetching practice log:', error);
      throw error;
    }
    
    console.log('✅ Practice log fetched:', data?.length || 0, 'entries');
    return data || [];
    
  } catch (error) {
    console.error('❌ Get practice log error:', error);
    return [];
  }
};

// ===== BADGE FUNCTIONS =====

// Award badge to user
export const awardBadge = async (userId, badgeName) => {
  try {
    console.log('🏆 Awarding badge to user:', userId, badgeName);
    
    const badgeData = {
      user_id: userId,
      badge_name: badgeName,
      earned_at: new Date().toISOString() // Using earned_at instead of awarded_at
    };
    
    const { data, error } = await supabase
      .from('user_badges')
      .insert(badgeData)
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error awarding badge:', error);
      throw error;
    }
    
    console.log('✅ Badge awarded successfully');
    return data;
    
  } catch (error) {
    console.error('❌ Award badge error:', error);
    throw error;
  }
};

// Fetch user badges
export const fetchUserBadges = async (userId) => {
  try {
    console.log('🔄 Fetching badges for user:', userId);
    
    const { data, error } = await supabase
      .from('user_badges')
      .select('badge_name')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false }); // Using earned_at instead of awarded_at
    
    if (error) {
      console.error('❌ Error fetching badges:', error);
      throw error;
    }
    
    const badges = data?.map(badge => badge.badge_name) || [];
    console.log('✅ Badges fetched:', badges.length);
    return badges;
    
  } catch (error) {
    console.error('❌ Fetch badges error:', error);
    return [];
  }
};

export const fetchSessionStatus = fetchSessionStatusFast;
export const fetchAllUserSessions = fetchDashboardDataFast;
export const savePainLevelWithAnalytics = savePainLevel;
export const updateVideoProgressWithAnalytics = updateSessionProgress;
export const bulkUpdateSessions = batchUpdateSession;

// Aliases for backward compatibility
export const signInWithEmailAndPassword = signIn;
export const supabaseSignOut = signOut;
export const saveUserToSupabase = saveUserProfile;

// Export the supabase client
export { supabase };
export default supabase;

// Add these functions to your existing src/supabase.js file

// ===== REDCAP ASSESSMENT FUNCTIONS =====

// Get participant assessment links by user ID
export const getParticipantAssessment = async (userId) => {
  try {
    console.log('🔄 Fetching participant assessment for user:', userId);
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error fetching participant assessment:', error);
      return null;
    }
    
    console.log('✅ Participant assessment fetched:', data ? 'Found' : 'Not found');
    return data;
    
  } catch (error) {
    console.error('❌ Get participant assessment error:', error);
    return null;
  }
};

// Get participant assessment by participant ID (for manual lookup)
export const getParticipantAssessmentByParticipantId = async (participantId) => {
  try {
    console.log('🔄 Fetching assessment for participant ID:', participantId);
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .select('*')
      .eq('participant_id', participantId)
      .single();
    
    if (error) {
      console.error('❌ Error fetching assessment by participant ID:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Assessment found for participant:', participantId);
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ Get assessment by participant ID error:', error);
    return { success: false, error: error.message };
  }
};

// Track assessment link clicks
export const trackAssessmentClick = async (userId, assessmentType) => {
  try {
    console.log(`🔄 Tracking ${assessmentType} assessment click for user:`, userId);
    
    const updateData = {
      [`${assessmentType}_assessment_link_clicked`]: true,
      [`${assessmentType}_assessment_clicked_timestamp`]: Date.now(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .update(updateData)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) {
      console.error(`❌ Error tracking ${assessmentType} assessment click:`, error);
      return false;
    }
    
    console.log(`✅ ${assessmentType} assessment click tracked`);
    return true;
    
  } catch (error) {
    console.error(`❌ Track ${assessmentType} assessment click error:`, error);
    return false;
  }
};

// Mark assessment as completed
export const markAssessmentCompleted = async (userId, assessmentType) => {
  try {
    console.log(`🔄 Marking ${assessmentType} assessment completed for user:`, userId);
    
    const updateData = {
      [`${assessmentType}_completed`]: true,
      [`${assessmentType}_completed_at`]: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .update(updateData)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) {
      console.error(`❌ Error marking ${assessmentType} assessment completed:`, error);
      return false;
    }
    
    // Also update the user_sessions table for tracking
    if (assessmentType === 'pre') {
      // Mark pre-assessment completed in user_sessions for session 1
      await updateSessionProgress(userId, 1, {
        pre_assessment_completed: true,
        pre_assessment_completion_timestamp: Date.now(),
        session_started: true, // Pre-assessment completion starts the session
        updated_at: new Date().toISOString()
      });
    // } else if (assessmentType === 'post') {
    //   // Mark post-assessment completed - this could be tied to the last session
    //   await updateSessionProgress(userId, 5, {
    //     post_assessment_completed: true,
    //     post_assessment_completion_timestamp: Date.now(),
    //     session_fully_completed: true, // Post-assessment completion finishes the program
    //     updated_at: new Date().toISOString()
    //   });
    }
    
    console.log(`✅ ${assessmentType} assessment marked as completed`);
    return true;
    
  } catch (error) {
    console.error(`❌ Mark ${assessmentType} assessment completed error:`, error);
    return false;
  }
};

// Bulk insert participant assessments (for initial setup)
export const bulkInsertParticipantAssessments = async (participantsArray) => {
  try {
    console.log('🔄 Bulk inserting participant assessments:', participantsArray.length);
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .insert(participantsArray)
      .select();
    
    if (error) {
      console.error('❌ Error bulk inserting participant assessments:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Participant assessments inserted successfully:', data.length);
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ Bulk insert participant assessments error:', error);
    return { success: false, error: error.message };
  }
};

// Link participant assessment to user account
export const linkParticipantToUser = async (participantId, userId) => {
  try {
    console.log('🔄 Linking participant to user:', participantId, userId);
    
    const { data, error } = await supabase
      .from('participant_assessments')
      .update({ user_id: userId, updated_at: new Date().toISOString() })
      .eq('participant_id', participantId)
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error linking participant to user:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Participant linked to user successfully');
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ Link participant to user error:', error);
    return { success: false, error: error.message };
  }
};

// Get all participants (for researcher dashboard)
export const getAllParticipantAssessments = async (sessionDate = null) => {
  try {
    console.log('🔄 Fetching all participant assessments');
    
    let query = supabase
      .from('participant_assessments')
      .select(`
        *,
        users (
          username,
          email,
          role
        )
      `)
      .order('created_at', { ascending: true });
    
    if (sessionDate) {
      query = query.eq('session_date', sessionDate);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Error fetching all participant assessments:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ All participant assessments fetched:', data.length);
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ Get all participant assessments error:', error);
    return { success: false, error: error.message };
  }
};

// Get assessment completion statistics
export const getAssessmentCompletionStats = async (sessionDate = null) => {
  try {
    console.log('🔄 Getting assessment completion statistics');
    
    let query = supabase
      .from('participant_assessments')
      .select('pre_completed, post_completed, participant_id');
    
    if (sessionDate) {
      query = query.eq('session_date', sessionDate);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Error getting completion stats:', error);
      return {
        total_participants: 0,
        pre_completed: 0,
        post_completed: 0,
        both_completed: 0,
        completion_rate: 0
      };
    }
    
    const stats = {
      total_participants: data.length,
      pre_completed: data.filter(p => p.pre_completed).length,
      post_completed: data.filter(p => p.post_completed).length,
      both_completed: data.filter(p => p.pre_completed && p.post_completed).length,
      completion_rate: data.length > 0 ? 
        (data.filter(p => p.pre_completed && p.post_completed).length / data.length) * 100 : 0
    };
    
    console.log('✅ Assessment completion stats:', stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Get completion stats error:', error);
    return {
      total_participants: 0,
      pre_completed: 0,
      post_completed: 0,
      both_completed: 0,
      completion_rate: 0
    };
  }
};

/**
 * Check if a specific session is unlocked for a participant
 * @param {string} userId - The user's ID
 * @param {number} sessionNumber - Session number (1-5)
 * @returns {Object} - {isUnlocked: boolean, unlockTime: timestamp, daysRemaining: number}
 */
export const checkSessionUnlock = async (userId, sessionNumber) => {
  try {
    console.log(`Checking unlock status for user ${userId}, session ${sessionNumber}`);
    
    // Get the participant's session schedule
    const { data: schedule, error } = await supabase
      .from('participant_session_schedule')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error || !schedule) {
      console.log('No session schedule found - defaulting to unlocked');
      return {
        isUnlocked: true, // Default to unlocked if no schedule is set
        unlockTime: null,
        daysRemaining: 0,
        message: 'No schedule configured'
      };
    }

    // Get the unlock time for the requested session
    const unlockTimeField = `session_${sessionNumber}_unlock_time`;
    const unlockTime = schedule[unlockTimeField];
    
    if (!unlockTime) {
      return {
        isUnlocked: false,
        unlockTime: null,
        daysRemaining: null,
        message: 'Session not scheduled'
      };
    }

    const now = new Date();
    const unlock = new Date(unlockTime);
    const isUnlocked = now >= unlock;
    
    // Calculate days remaining if locked
    const daysRemaining = isUnlocked ? 0 : Math.ceil((unlock - now) / (1000 * 60 * 60 * 24));
    
    return {
      isUnlocked,
      unlockTime: unlock,
      daysRemaining,
      message: isUnlocked ? 'Session unlocked' : `Unlocks in ${daysRemaining} days`
    };
    
  } catch (error) {
    console.error('Error checking session unlock:', error);
    return {
      isUnlocked: true, // Default to unlocked on error
      unlockTime: null,
      daysRemaining: 0,
      message: 'Error checking schedule'
    };
  }
};

/**
 * Get all unlock times for a participant
 * @param {string} userId - The user's ID
 * @returns {Object} - Complete schedule info
 */
export const getParticipantSchedule = async (userId) => {
  try {
    const { data: schedule, error } = await supabase
      .from('participant_session_schedule')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error || !schedule) {
      return null;
    }

    return {
      startDate: schedule.session_1_start_date,
      session1Unlock: schedule.session_1_unlock_time,
      session2Unlock: schedule.session_2_unlock_time,
      session3Unlock: schedule.session_3_unlock_time,
      session4Unlock: schedule.session_4_unlock_time,
      session5Unlock: schedule.session_5_unlock_time,
      notes: schedule.notes
    };
    
  } catch (error) {
    console.error('Error getting participant schedule:', error);
    return null;
  }
};